import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { rpc, useAction, useOrg, usePeople, type PersonLite } from "@vixio/data";
import { PersonPicker } from "@vixio/core-ui";
import { Button, Checkbox, Dialog, NumberInput, TextInput } from "@vixio/ui";
import type { Team } from "./api";

/** Criar ou editar equipe do Pulso. */
export function TeamDialog({ team, open, onOpenChange }: { team: Team | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const navigate = useNavigate();
  const people = usePeople(org.id, [team?.manager_person_id]);
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [manager, setManager] = useState<PersonLite | null>(null);
  const [cadence, setCadence] = useState<number | null>(14);
  const [minResp, setMinResp] = useState<number | null>(5);
  const [drop, setDrop] = useState<number | null>(10);
  const [active, setActive] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(team?.name ?? "");
    setClient(team?.client_name ?? "");
    setCadence(team?.cadence_days ?? 14);
    setMinResp(team?.min_responses ?? 5);
    setDrop(team?.drop_threshold ?? 10);
    setActive(team?.active ?? true);
    setSubmitted(false);
    setManager(null);
  }, [open, team]);
  useEffect(() => {
    if (open && team?.manager_person_id && !manager) setManager(people.data?.get(team.manager_person_id) ?? null);
  }, [open, team, people.data, manager]);

  const errors = {
    name: name.trim().length < 2 ? "Informe o nome da equipe." : null,
    cadence: cadence === null || cadence < 7 || cadence > 31 ? "Entre 7 e 31 dias." : null,
    min: minResp === null || minResp < 5 ? "O mínimo é 5 respostas (anonimato)." : null,
    drop: drop === null || drop < 1 || drop > 100 ? "Entre 1 e 100 pontos." : null,
  };
  const invalid = Object.values(errors).some(Boolean);

  const save = useAction(
    async () => {
      if (team) {
        await rpc("pulso", "update_team", {
          p_team: team.id,
          p_name: name.trim(),
          p_client_name: client.trim(),
          p_manager_person: manager?.id ?? null,
          p_cadence_days: cadence,
          p_min_responses: minResp,
          p_drop_threshold: drop,
          p_active: active,
        });
        return team.id;
      }
      return rpc("pulso", "create_team", {
        p_org: org.id,
        p_name: name.trim(),
        p_client_name: client.trim() || null,
        p_manager_person: manager?.id ?? null,
        p_cadence_days: cadence ?? 14,
        p_min_responses: minResp ?? 5,
        p_drop_threshold: drop ?? 10,
      });
    },
    {
      invalidate: [["pulso"]],
      success: team ? "Equipe atualizada" : "Equipe criada",
      onSuccess: (id) => {
        onOpenChange(false);
        if (!team) navigate(`/diagnostico/pulso/${id}`);
      },
    },
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={team ? "Editar equipe" : "Nova equipe"}
      description="Equipe de um cliente. O resultado só aparece com o ciclo encerrado e o mínimo de respostas."
      onSubmit={() => {
        setSubmitted(true);
        if (!invalid) save.mutate(undefined);
      }}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {team ? "Salvar" : "Criar equipe"}
          </Button>
        </>
      }
    >
      <div className="vx-stack">
        <TextInput label="Nome da equipe" value={name} onChange={setName} required maxLength={120} error={submitted ? errors.name : null} autoFocus />
        <TextInput label="Cliente" value={client} onChange={setClient} maxLength={200} hint="Como aparece no painel e no alerta." />
        <PersonPicker label="Gestor que recebe o alerta de queda" value={manager} onChange={setManager} hint="Pessoa do cadastro único. Opcional." />
        <div className="vx-form-grid">
          <NumberInput label="Cadência" value={cadence} onChange={setCadence} min={7} max={31} suffix="dias" error={submitted ? errors.cadence : null} />
          <NumberInput label="Mínimo de respostas" value={minResp} onChange={setMinResp} min={5} suffix="respostas" error={submitted ? errors.min : null} hint="Nunca abaixo de 5." />
          <NumberInput label="Alerta de queda" value={drop} onChange={setDrop} min={1} max={100} suffix="pontos" error={submitted ? errors.drop : null} />
        </div>
        {team ? <Checkbox label="Equipe ativa" hint="Inativa não abre novos ciclos pelo agendador." checked={active} onChange={setActive} /> : null}
      </div>
    </Dialog>
  );
}
